// @flow
/* eslint-disable ta-code-conventions/has-matching-storydoc */
/* eslint-disable ta-code-conventions/has-matching-test */
/* eslint-disable ta-code-conventions/require-jsdoc */

import React, { useCallback, useContext, useEffect, useRef, useState, memo } from 'react';
import classNames from '@ta/common.classnames';
import { useVisibleEffect } from '@ta/common.is-visible';
import { Device, Environment } from '@ta/lithium.context';
import DayPicker from 'react-day-picker';
import {
  isFirstOfMonth,
  isLastOfMonth,
  useLocaleUtils,
  getDifferenceDays,
  today,
} from './dateUtils';
import styles from './DatePicker.css';

type Props = {|
  /**
   * The maximum number of days from startDate to endDate, inclusive
   */
  maxRangeDays?: number,

  /**
   * An optional callback to scroll the scrolling element that this date picker is within. If
   * supplied, the selected start date will be scrolled into view on mount.
   */
  scrollContainer?: number => mixed,

  startDate: ?Date,
  endDate: ?Date,

  onStartDateChanged: (?Date) => mixed,
  onEndDateChanged: (?Date) => mixed,
|};
/**
 * A calendar that only renders while it's on screen. react-day-picker drops a ton of frames if we
 * try to render 24+ calendars at the same time
 */
const LazyCalendar = ({
  visibleByDefault,
  scrollIntoViewByDefault,
  scrollContainer,
  ...dayPickerProps
}: {
  /**
   * If true, render this initially instead of waiting for the Intersection Observer API to say
   * it's visible
   */
  visibleByDefault: boolean,
  scrollIntoViewByDefault: boolean,
  scrollContainer?: number => mixed,
}) => {
  const [isVisible, setIsVisible] = useState(visibleByDefault || scrollIntoViewByDefault);
  const ref = useVisibleEffect(setIsVisible);

  // If the start date is within this calendar, scroll the container to it's initially in the
  // viewport
  const scrollIntoView = useRef(scrollIntoViewByDefault);
  useEffect(() => {
    if (ref.current != null && scrollContainer != null && scrollIntoView.current) {
      scrollContainer(ref.current.offsetTop - 120);
    }
  }, [scrollContainer, ref]);

  return (
    <div
      ref={ref}
      className={classNames(styles.lazyCalendarWrapper, { [styles.visible]: isVisible })}
    >
      {isVisible && <DayPicker {...dayPickerProps} />}
    </div>
  );
};

/**
 * A date picker that supports selecting an exact range of days
 */
const DatePicker = ({
  maxRangeDays = 30,
  scrollContainer,
  startDate,
  endDate,
  onStartDateChanged,
  onEndDateChanged,
}: Props) => {
  const { locale } = useContext(Environment);
  const localeUtils = useLocaleUtils();

  const dayClicked = (date: Date, { [styles.disabled]: disabled }) => {
    if (disabled) {
      return;
    }

    // If there are no dates, a range of dates, or a far away date already selected, select only
    // the clicked date.
    if (
      startDate == null ||
      endDate == null ||
      startDate.getTime() !== endDate.getTime() ||
      getDifferenceDays(startDate, date) >= maxRangeDays
    ) {
      onStartDateChanged(date);
      onEndDateChanged(date);
    }
    // If the clicked date is already the entire selection, deselect it
    else if (startDate.getTime() === date.getTime()) {
      onStartDateChanged(null);
      onEndDateChanged(null);
    }
    // Otherwise, select a range of days from the existing selected day to the clicked day
    else if (startDate < date) {
      onEndDateChanged(date);
    } else {
      onStartDateChanged(date);
    }
  };

  // On iOS, simulate a click immediately on touchend. There's normally a delay between tapping and
  // the click event, and on iOS the touch-action property doesn't disable this delay.
  const { os } = useContext(Device);
  const dayTouchEnd = useCallback(
    (ignored1, ignored2, evt: SyntheticTouchEvent<*>) => {
      if (os.family === 'IOS') {
        evt.currentTarget.click();
        evt.preventDefault();
      }
    },
    [os],
  );

  return (
    <>
      <div className={styles.daysOfWeek}>
        {[0, 1, 2, 3, 4, 5, 6].map(weekday => (
          <div key={weekday} className={styles.dayOfWeek}>
            {localeUtils.formatWeekdayShort(weekday)}
          </div>
        ))}
      </div>

      {Array(20)
        .fill(0)
        .map((_, offset) => new Date(today.getFullYear(), today.getMonth() + offset, 1))
        .map((month, index) => (
          <LazyCalendar
            key={month.toISOString()}
            visibleByDefault={index === 0}
            scrollIntoViewByDefault={
              startDate != null &&
              startDate.getFullYear() === month.getFullYear() &&
              startDate.getMonth() === month.getMonth()
            }
            scrollContainer={scrollContainer}
            month={month}
            canChangeMonth={false}
            showWeekDays={false}
            localeUtils={localeUtils}
            locale={locale.code}
            disabledDays={{
              before: today,
            }}
            selectedDays={{
              from: startDate,
              to: endDate,
            }}
            modifiers={{
              [styles.startDate]: [startDate, isFirstOfMonth],
              [styles.endDate]: [endDate, isLastOfMonth],
            }}
            classNames={{
              caption: styles.monthName,
              week: styles.week,
              day: styles.day,
              disabled: styles.disabled,
              outside: styles.disabled,
              selected: styles.selected,
              month: '',
              container: '',
              wrapper: '',
              interactionDisabled: '',
              body: '',
              today: '',
            }}
            onDayClick={dayClicked}
            onDayTouchEnd={dayTouchEnd}
          />
        ))}
    </>
  );
};

export default memo<Props>(DatePicker);
